import { useRef } from 'react';

// Module-level cache map. Survives soft re-mounts (within an SPA navigation)
// and is rebuilt from sessionStorage on hard reload (e.g. iOS swipe-back when
// bfcache isn't available).
const moduleCache = new Map<string, Entry>();
const STORAGE_PREFIX = 'list_cache_';

/**
 * Wie lange ein zwischengespeicherter Listenstand gelten darf.
 *
 * Der Cache soll das Nachladen beim Zurückwischen ersparen — mehr nicht. Ohne Ablauf hält ein
 * lange offener Tab seinen Stand unbegrenzt fest: nach einem Sync (oder einem Datenimport) sieht
 * derselbe Nutzer weiter die alte Liste, während ein frischer Tab die neue zeigt. Eine Minute
 * deckt den Navigationsfall vollständig ab und begrenzt die Veraltung auf ein erträgliches Maß.
 */
const MAX_AGE_MS = 60_000;

interface Entry { at: number; value: unknown; owner?: string | number }

const fresh = (e: Entry | null | undefined): boolean =>
  !!e && Date.now() - e.at < MAX_AGE_MS;

const readFromStorage = (key: string): Entry | null => {
  try {
    const raw = typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(STORAGE_PREFIX + key)
      : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Entry;
    // Ältere Einträge ohne Zeitstempel (aus einer früheren Version) gelten als abgelaufen.
    return typeof parsed?.at === 'number' ? parsed : null;
  } catch { return null; }
};

const writeToStorage = (key: string, entry: Entry | null) => {
  try {
    if (entry === null) sessionStorage.removeItem(STORAGE_PREFIX + key);
    else sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
  } catch { /* quota / private mode */ }
};

/**
 * Cache list data across navigations to skip the refetch + flicker on swipe-back.
 *
 * Usage:
 *   const { cache, setCache, fromCache } = useListCache<MyData>('mylist');
 *   const [items, setItems] = useState(cache?.items ?? []);
 *   const [loading, setLoading] = useState(!cache);
 *   // suppress entry animations if fromCache:
 *   const cls = fromCache ? '' : 'animate-fade-up';
 *   // when navigating away:
 *   setCache({ items, ... });
 */
/** Invalidate a cached list from outside the hook (e.g. after a mutation on another page). */
export function clearListCache(storageKey: string) {
  moduleCache.delete(storageKey);
  writeToStorage(storageKey, null);
}

export function useListCache<T>(storageKey: string, owner?: string | number) {
  const initial = useRef<T | null>((() => {
    const entry = moduleCache.get(storageKey) ?? readFromStorage(storageKey);
    if (!fresh(entry) || entry?.owner !== owner) {
      // Abgelaufenes gleich wegräumen, sonst liegt es bis zum Tab-Ende herum.
      if (entry) clearListCache(storageKey);
      return null;
    }
    return entry!.value as T;
  })());
  // Snapshot whether we hydrated from cache, so it stays stable across renders
  const fromCacheRef = useRef<boolean>(initial.current !== null);
  const ownerRef = useRef(owner);
  if (ownerRef.current !== owner) {
    initial.current = null;
    fromCacheRef.current = false;
    ownerRef.current = owner;
  }

  const setCache = (value: T | null) => {
    if (value === null) { clearListCache(storageKey); return; }
    const entry: Entry = { at: Date.now(), value, owner };
    moduleCache.set(storageKey, entry);
    writeToStorage(storageKey, entry);
  };

  return {
    cache: initial.current,
    setCache,
    fromCache: fromCacheRef.current,
  };
}
